import React from 'react';
import { EllipsisTooltip, SearchInput } from '@/components';
import {
  AIChatBookmarkIcon,
  CloseDrawerIcon,
  OneTimeArrowIcon,
} from '@/assets/icons/icons';
import type { AIChatSessionGroup, SavedQuestion } from '../types';

type ComposerProps = {
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  value: string;
  onChange: (value: string) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onSave: () => void;
  onSend: () => void;
  isSaved: boolean;
  saveDisabled: boolean;
  sendDisabled: boolean;
  ariaLabel: string;
  placeholder?: string;
  disabled?: boolean;
  expanded?: boolean;
  followup?: boolean;
};

export const AIChatComposer: React.FC<ComposerProps> = ({
  textareaRef,
  value,
  onChange,
  onKeyDown,
  onSave,
  onSend,
  isSaved,
  saveDisabled,
  sendDisabled,
  ariaLabel,
  placeholder,
  disabled,
  expanded,
  followup,
}) => (
  <section
    className={`ai-chat-drawer__composer ${
      followup ? 'ai-chat-drawer__composer--followup' : ''
    } ${expanded ? 'ai-chat-drawer__composer--expanded' : ''}`}
    aria-label={ariaLabel}
    onClick={(event) => {
      // The textarea sits bottom-aligned in this box (min-height with
      // justify-content: flex-end), so short text leaves empty space above
      // it that's visually part of the input but isn't the textarea
      // itself — clicking there should still focus it, like clicking
      // anywhere in a native input's box would.
      if (event.target === event.currentTarget) {
        textareaRef.current?.focus();
      }
    }}
  >
    <textarea
      ref={textareaRef}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={onKeyDown}
      disabled={disabled}
      placeholder={placeholder}
      aria-label={ariaLabel}
      rows={3}
    />
    <div className='ai-chat-drawer__composer-actions'>
      <button
        type='button'
        className={`ai-chat-drawer__square-btn ai-chat-drawer__square-btn--save ${
          isSaved ? 'is-active' : ''
        }`}
        onClick={onSave}
        disabled={saveDisabled}
        aria-label='Save question'
      >
        <AIChatBookmarkIcon color='currentColor' />
      </button>
      <button
        type='button'
        className='ai-chat-drawer__square-btn'
        onClick={onSend}
        disabled={sendDisabled}
        aria-label='Send question'
      >
        <OneTimeArrowIcon fill='currentColor' />
      </button>
    </div>
  </section>
);

type SavedQuestionsSectionProps = {
  savedQuestions: SavedQuestion[];
  rows: SavedQuestion[][];
  showAll: boolean;
  savingQuestion: boolean;
  expanded?: boolean;
  onSendQuestion: (question: string) => void;
  onRemoveQuestion: (question: SavedQuestion) => void;
  onToggleShowAll: () => void;
};

export const AIChatSavedQuestionsSection: React.FC<
  SavedQuestionsSectionProps
> = ({
  savedQuestions,
  rows,
  showAll,
  savingQuestion,
  expanded,
  onSendQuestion,
  onRemoveQuestion,
  onToggleShowAll,
}) => {
  if (savedQuestions.length === 0) return null;

  return (
    <section
      className={`ai-chat-drawer__question-section ${
        expanded ? 'ai-chat-drawer__question-section--expanded' : ''
      }`}
    >
      <div className='ai-chat-drawer__section-label'>Saved questions</div>
      <div
        className={`ai-chat-drawer__chips ai-chat-drawer__chips--saved ${
          expanded ? 'ai-chat-drawer__chips--expanded-saved' : ''
        }`}
      >
        {rows.map((row) => (
          <div
            key={row.map((item) => item.id).join('-')}
            className='ai-chat-drawer__suggested-row'
          >
            {row.map((savedQuestion) => (
              <span
                key={savedQuestion.id}
                className='ai-chat-drawer__chip ai-chat-drawer__expanded-saved-chip'
              >
                <button
                  type='button'
                  className='ai-chat-drawer__saved-chip-text'
                  onClick={() => onSendQuestion(savedQuestion.question)}
                >
                  {savedQuestion.question}
                </button>
                <button
                  type='button'
                  className='ai-chat-drawer__remove-chip'
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onRemoveQuestion(savedQuestion);
                  }}
                  disabled={savingQuestion}
                  aria-label={`Remove saved question: ${savedQuestion.question}`}
                >
                  <CloseDrawerIcon size={12} />
                </button>
              </span>
            ))}
          </div>
        ))}
      </div>
      {savedQuestions.length > 4 && (
        <button
          type='button'
          className='ai-chat-drawer__show-all'
          onClick={onToggleShowAll}
        >
          {showAll ? 'Show less' : `Show all (${savedQuestions.length})`}
        </button>
      )}
    </section>
  );
};

type SuggestedQuestionsSectionProps = {
  rows: string[][];
  expanded?: boolean;
  onSendQuestion: (question: string) => void;
};

export const AIChatSuggestedQuestionsSection: React.FC<
  SuggestedQuestionsSectionProps
> = ({ rows, expanded, onSendQuestion }) => (
  <section
    className={`ai-chat-drawer__question-section ${
      expanded ? 'ai-chat-drawer__question-section--expanded' : ''
    }`}
  >
    <div className='ai-chat-drawer__section-label'>Suggested</div>
    <div
      className={`ai-chat-drawer__chips ai-chat-drawer__chips--suggested ${
        expanded ? 'ai-chat-drawer__chips--suggested-expanded' : ''
      }`}
    >
      {rows.map((row) => (
        <div key={row.join('-')} className='ai-chat-drawer__suggested-row'>
          {row.map((suggestion) => (
            <button
              key={suggestion}
              type='button'
              className='ai-chat-drawer__chip'
              onClick={() => onSendQuestion(suggestion)}
            >
              {suggestion}
            </button>
          ))}
        </div>
      ))}
    </div>
  </section>
);

type HistoryGroupsProps = {
  groups: AIChatSessionGroup[];
  activeSessionId: string | null;
  loading: boolean;
  itemClassName: string;
  groupClassName: string;
  labelClassName: string;
  emptyClassName: string;
  onSelectSession: (sessionId: string) => void;
};

export const AIChatHistoryGroups: React.FC<HistoryGroupsProps> = ({
  groups,
  activeSessionId,
  loading,
  itemClassName,
  groupClassName,
  labelClassName,
  emptyClassName,
  onSelectSession,
}) => {
  const hasItems = groups.some((group) => group.items.length > 0);

  if (!hasItems) {
    return (
      <div className={emptyClassName}>
        {loading ? 'Loading chats...' : 'No chats found'}
      </div>
    );
  }

  return (
    <>
      {groups.map((group) => (
        <div key={group.label} className={groupClassName}>
          <div className={labelClassName}>{group.label}</div>
          {group.items.map((chatSession) => (
            <button
              key={chatSession.id}
              type='button'
              className={`${itemClassName} ${
                chatSession.id === activeSessionId ? 'is-active' : ''
              }`}
              onClick={() => onSelectSession(chatSession.id)}
            >
              <EllipsisTooltip
                text={chatSession.title}
                className='ai-chat-drawer__history-item-text'
                zIndex={2200}
              />
            </button>
          ))}
        </div>
      ))}
    </>
  );
};

type HistorySearchProps = {
  value: string;
  onChange: (value: string) => void;
  className: string;
};

export const AIChatHistorySearch: React.FC<HistorySearchProps> = ({
  value,
  onChange,
  className,
}) => (
  <div className={className}>
    <SearchInput
      placeholder='Search chats'
      defaultValue={value}
      onChange={onChange}
      onClear={() => onChange('')}
      width='100%'
      style={{
        height: 36,
        width: '100%',
        padding: '0 16px',
      }}
    />
  </div>
);
